<template>
  <b-card class="lot-calculator">
    <template #header>
      <div class="header">
        <span>Lot Free Calculator</span>
        <button
          class="reset-btn"
          @click="reset"
        >
          <feather-icon
            size="14"
            icon="RotateCwIcon"
            color="white"
          />
        </button>
      </div>
    </template>

    <b-card-body>
      <!-- Tabs -->
      <div class="tabs">
        <button
          :class="{ active: calculationMode === 0 }"
          @click="calculationMode = 0"
        >
          Calculate Lot
        </button>
        <button
          :class="{ active: calculationMode === 1 }"
          @click="calculationMode = 1"
        >
          Calculate Dis %
        </button>
      </div>

      <!-- Input -->
      <div class="input-section">
        <input
          v-if="calculationMode === 0"
          v-model.number="discountPercent"
          type="number"
          step="0.01"
          placeholder="Enter discount %"
          @keyup.enter="handleCalculate"
        >
        <input
          v-if="calculationMode === 1"
          v-model="lotScheme"
          type="text"
          placeholder="e.g., 9+1"
          @keyup.enter="handleCalculate"
        >
        <button
          class="calc-btn"
          @click="handleCalculate"
        >
          <feather-icon
            size="14"
            icon="CalculatorIcon"
          />
          Calculate
        </button>
      </div>

      <!-- Results -->
      <div
        v-if="results.calculated"
        class="results"
      >
        <div class="main-result">
          <div class="value">
            {{ results.displayValue }}
          </div>
          <div class="label">
            {{ results.label }}
          </div>
        </div>

        <div
          v-if="results.details"
          class="details"
        >
          <div class="row">
            <span>Paid:</span>
            <span>{{ results.paidQty }}</span>
          </div>
          <div class="row">
            <span>Free:</span>
            <span>{{ results.freeQty }}</span>
          </div>
          <div class="row">
            <span>Total:</span>
            <span>{{ results.totalUnits }}</span>
          </div>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody } from 'bootstrap-vue'

export default {
  name: 'LOTCalculator',
  components: { BCard, BCardBody },
  data() {
    return {
      calculationMode: 0,
      discountPercent: null,
      lotScheme: '',
      results: {
        calculated: false,
        displayValue: '',
        label: '',
        freeQty: null,
        paidQty: null,
        totalUnits: null,
        details: false,
      },
    }
  },
  watch: {
    calculationMode() {
      this.reset()
    },
  },
  methods: {
    handleCalculate() {
      if (this.calculationMode === 0) {
        this.calculateLot()
      } else {
        this.calculateDiscount()
      }
    },
    calculateLot() {
      if (this.discountPercent > 0) {
        const referenceQty = 100
        const freeQty = referenceQty * (this.discountPercent / 100)
        const paidQty = referenceQty - freeQty

        if (freeQty <= 0) {
          this.results = {
            calculated: true,
            displayValue: 'No Lot',
            label: 'Discount too low',
            details: false,
          }
          return
        }

        const lotScheme = this.simplifyToLotScheme(paidQty, freeQty)

        this.results = {
          calculated: true,
          displayValue: lotScheme,
          label: 'Lot Scheme',
          freeQty: freeQty.toFixed(2),
          paidQty: paidQty.toFixed(2),
          totalUnits: referenceQty,
          details: true,
        }
      }
    },
    calculateDiscount() {
      if (this.lotScheme) {
        const parts = this.lotScheme.trim().split('+')
        const paidUnits = parseInt(parts[0], 10)
        const freeUnits = parseInt(parts[1], 10)

        if (paidUnits > 0 && freeUnits > 0) {
          const totalUnits = paidUnits + freeUnits
          const discountPercent = (freeUnits / totalUnits) * 100

          this.results = {
            calculated: true,
            displayValue: `${discountPercent.toFixed(2)}%`,
            label: 'Discount %',
            freeQty: freeUnits,
            paidQty: paidUnits,
            totalUnits,
            details: true,
          }
        }
      }
    },
    simplifyToLotScheme(paidQty, freeQty) {
      const { discountPercent } = this
      let bestScheme = null
      let smallestDiff = Infinity

      for (let total = 10; total <= 100; total += 1) {
        const free = Math.round((total * discountPercent) / 100)
        const paid = total - free

        if (free > 0 && paid > 0) {
          const actualDiscount = (free / total) * 100
          const diff = Math.abs(actualDiscount - discountPercent)

          if (diff < smallestDiff) {
            smallestDiff = diff
            bestScheme = { paid, free }
          }

          if (diff < 0.01) break
        }
      }

      if (bestScheme) {
        const gcd = this.findGCD(bestScheme.paid, bestScheme.free)
        return `${bestScheme.paid / gcd}+${bestScheme.free / gcd}`
      }

      return `${Math.round(paidQty / freeQty)}+1`
    },
    findGCD(a, b) {
      let x = Math.abs(Math.round(a))
      let y = Math.abs(Math.round(b))
      while (y !== 0) {
        const temp = y
        y = x % y
        x = temp
      }
      return x
    },
    reset() {
      this.discountPercent = null
      this.lotScheme = ''
      this.results = {
        calculated: false,
        displayValue: '',
        label: '',
        freeQty: null,
        paidQty: null,
        totalUnits: null,
        details: false,
      }
    },
  },
}
</script>

<style scoped>
.lot-calculator {
  max-width: 400px;
  margin: 0 auto;
}

/* Header */
.lot-calculator >>> .card-header {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  color: white;
  padding: 4px 12px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header span {
  font-size: 0.85rem;
  font-weight: 600;
}

.reset-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;
}

.reset-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Body */
.lot-calculator >>> .card-body {
  padding: 0;
}

/* Tabs */
.tabs {
  display: flex;
  background: #f5f5f5;
}

.tabs button {
  flex: 1;
  padding: 10px;
  background: none;
  border: none;
  font-size: 0.8rem;
  font-weight: 500;
  color: #666;
  cursor: pointer;
}

.tabs button.active {
  background: white;
  color: #17a2b8;
  border-bottom: 2px solid #17a2b8;
}

/* Input Section */
.input-section {
  padding: 12px;
}

.input-section input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
}

.calc-btn {
  width: 100%;
  padding: 8px;
  margin-top: 8px;
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* Results */
.results {
  background: #f8f9fa;
  padding: 12px;
  border-top: 1px solid #ddd;
}

.main-result {
  text-align: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #ddd;
  margin-bottom: 10px;
}

.main-result .value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

.main-result .label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  margin-top: 4px;
}

.details .row {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 6px;
  font-size: 0.8rem;
}

.details .row span:first-child {
  color: #666;
}

.details .row span:last-child {
  color: #333;
  font-weight: 600;
}
</style>
